/**
 * calculateETAs.js
 * 
 * This file contains functions to calculate estimated arrival times (ETAs)
 * for each inspection in the optimized routes. It takes the optimized routes
 * and the selected date as inputs, then populates the AppointmentStart and
 * AppointmentFinish fields for each inspection.
 */

/**
 * Calculates ETAs for each inspection in a single optimized route
 * 
 * @param {Object} optimizedRoute - Single optimized route object
 * @param {Date} selectedDate - The date for which to calculate ETAs
 * @returns {Object} - The optimized route with ETAs populated and lunchBreakStartTime added
 */
export function calculateETAs(optimizedRoute, selectedDate) {
    if (!optimizedRoute || !selectedDate) {
        console.warn('Invalid inputs provided to calculateETAs');
        return null;
    }

    // Skip if no inspections assigned
    if (!optimizedRoute.assignedInspections || optimizedRoute.assignedInspections.length === 0) {
        return optimizedRoute;
    }

    // Format the date to YYYY-MM-DD for the appointment times
    const formattedDate = selectedDate.toISOString().split('T')[0];
    
    // Create a deep copy of the route to avoid modifying the original
    const updatedRoute = JSON.parse(JSON.stringify(optimizedRoute));
    
    // Start time is 8:00 AM (8:00:00) - inspector must arrive at first inspection at 8:00 AM
    let currentTime = new Date(selectedDate);
    currentTime.setHours(8, 0, 0, 0);
    
    // Track if lunch break has been taken and when it started
    let lunchBreakTaken = false;
    let lunchBreakStartTime = null;
    
    // Process each inspection in the route
    updatedRoute.assignedInspections = updatedRoute.assignedInspections.map((inspection, index) => {
        // Set the appointment start time
        const appointmentStart = formatDateTime(currentTime);
        
        // Calculate the appointment end time based on the inspection duration
        const inspectionDurationMinutes = Math.ceil(inspection.importedDuration / 60);
        const endTime = new Date(currentTime);
        endTime.setMinutes(endTime.getMinutes() + inspectionDurationMinutes);
        const appointmentFinish = formatDateTime(endTime);
        
        // Update the current time to the end of this inspection
        currentTime = new Date(endTime);
        
        // Check if we need to take a lunch break after this inspection ends
        // If current time is after 11:00 AM and lunch break hasn't been taken yet
        if (!lunchBreakTaken && currentTime.getHours() >= 11 && currentTime.getMinutes() > 0) {
            // Capture the lunch break start time (immediately after inspection ends)
            lunchBreakStartTime = formatDateTime(currentTime);
            // Add 30 minutes for lunch break
            currentTime.setMinutes(currentTime.getMinutes() + 30);
            lunchBreakTaken = true;
        }
        
        // Add the leg time to the next inspection (if there is one)
        if (index < optimizedRoute.assignedInspections.length - 1 && optimizedRoute.legTimes && optimizedRoute.legTimes.length > index + 1) {
            const nextLegTimeMinutes = optimizedRoute.legTimes[index + 1];
            currentTime.setMinutes(currentTime.getMinutes() + nextLegTimeMinutes);
        }
        
        // Return the inspection with updated appointment times
        return {
            ...inspection,
            newAppointmentStart: appointmentStart,
            newAppointmentFinish: appointmentFinish
        };
    });
    
    // Add lunch break start time to the route object
    updatedRoute.breakTime = lunchBreakStartTime;
    
    return updatedRoute;
}

/**
 * Formats a Date object to the required string format (YYYY-MM-DD HH:MM:SS)
 * 
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
function formatDateTime(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
} 